import OrderDTO from "@/dto/order/OrderDTO";
import PartnershipDTO from "@/dto/partnership/PartnershipDTO";
import ShopDTO from "@/dto/shop/ShopDTO";
import DropShippingRefundStatus from "@/enum/DropShippingRefundStatus";
import OrderStatus from "@/enum/OrderStatus";
import ShopType from "@/enum/ShopType";
import { confirm } from "@/utils";
import moment from "moment";

/**判断一个店铺类型是否为供货商类型 */
export function getIsSupplierByShopType(shopType: ShopType) {
    switch (shopType) {
        case ShopType.JD_DROP_SHIPPING_SUPPLIER:
        case ShopType.OTHER_DROP_SHIPPING_SUPPLIER:
        case ShopType.PDD_DROP_SHIPPING_SUPPLIER:
        case ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER:
        case ShopType.DOUDIAN_DROP_SHIPPING_SUPPLIER:
            return true;

        default:
            return false;
    }
}

/**
 * 获取相对应的供货商店铺类型
 */
export function getCorrespondSupplierShopType(shopType: ShopType) {
    switch (shopType) {
        case ShopType.PINDUODUO:
        case ShopType.KTT:
            return ShopType.PDD_DROP_SHIPPING_SUPPLIER;
        case ShopType.ALIBABA:
        case ShopType.TAOBAO:
        case ShopType.ALI_C2M:
        case ShopType.TAOBAO_DISTRIBUTION:
            return ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER;
        case ShopType.JD:
        case ShopType.JD_PURCHASE:
            return ShopType.JD_DROP_SHIPPING_SUPPLIER;
        case ShopType.DOUDIAN:
        case ShopType.DOUDIAN_SUPPLIER:
            return ShopType.DOUDIAN_DROP_SHIPPING_SUPPLIER;
        default:
            return ShopType.OTHER_DROP_SHIPPING_SUPPLIER;
    }
}

/**
 * 是否为代发订单
 */
export function isDropshippingOrder(order: OrderDTO) {
    return !!order.retailerDropShippingInfo;
}

/** 分销订单是否可以退款 */
export function isOrderCanBeRefund(order: OrderDTO) {
    // 1. 如果已经部分发货，不能退款
    if (order.isPartShipped) {
        return false;
    }

    const info = order.retailerDropShippingInfo;
    // 2. 如果没有分销信息，不能退款
    if (!info) {
        return false;
    }

    const { orderStatus, refundStatus } = info;

    // 3. 只有「待付款」或「待发货」或「已发货」状态下，才允许申请退款
    const refundableStatuses = [OrderStatus.PENDING_PAYMENT, OrderStatus.PENDING_SHIPMENT, OrderStatus.SHIPPED];
    const isStatusAllowRefund = refundableStatuses.includes(orderStatus);

    // 4. 如果退款状态已经是「退款中」，则不能再次申请
    const isNotInPendingRefund = refundStatus !== DropShippingRefundStatus.PENDING_REFUND;

    return isStatusAllowRefund && isNotInPendingRefund;
}

export async function ensureAutoPartnershipAfterAction(
    retailerShop: ShopDTO,
    supplierShopId: number,
    canBeEstablishedPartnerships: PartnershipDTO[],
    actionName: string,
) {
    if (!retailerShop) return;
    if (!supplierShopId) return;
    if (!canBeEstablishedPartnerships?.length) return;

    const canBeEstablishedPartnership = canBeEstablishedPartnerships.find(
        (supplierPartnership) => supplierPartnership.shopId === supplierShopId,
    );

    if (canBeEstablishedPartnership) {
        await confirm(
            `当前店铺【${retailerShop.name}】和供货商【${
                canBeEstablishedPartnership?.alias || canBeEstablishedPartnership.name
            }】还没建立分销关系，${actionName}后自动建立分销关系`,
        );
    }
}

export function sortPartnership(a: PartnershipDTO, b: PartnershipDTO) {
    // 1. 优先使用Index排序
    const aIndex = a.index || 0;
    const bIndex = b.index || 0;

    if (aIndex || bIndex) {
        return aIndex - bIndex;
    }

    // 2. 其次根据合作状态排序

    //  如果 a 已取消，b 未取消，a 应排后面。
    if (a.suspended && !b.suspended) return 1;
    //  如果 b 已取消，a 未取消，b 应排后面。
    if (!a.suspended && b.suspended) return -1;

    // 3. 最后按合作时间排列。
    return moment(b.dateCooperated).diff(moment(a.dateCooperated));
}

export function splitPartnerships(partnerships: PartnershipDTO[]) {
    if (!partnerships) return { activePartnerships: [], suspendedPartnerships: [] };

    const activePartnerships = partnerships.filter((partnership) => !partnership.suspended);
    const suspendedPartnerships = partnerships.filter((partnership) => partnership.suspended);

    return { activePartnerships, suspendedPartnerships };
}
